import { ChangeDetectorRef, Component, DestroyRef, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { ProfileService, UserProfile } from './profile.service';
import { CommonModule } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { IconComponent } from '../shared/components/icon/icon.component';

@Component({
  selector: 'app-profile-edit',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, RouterModule, IconComponent],
  templateUrl: './profile-edit.component.html',
  styleUrls: ['./profile-edit.component.scss']
})
export class ProfileEditComponent implements OnInit {
  private destroyRef = inject(DestroyRef);

  private cdr = inject(ChangeDetectorRef);
  profileForm!: FormGroup;
  currentUser: UserProfile | null = null;
  errorMessage: string | null = null;
  successMessage: string | null = null;
  isLoading = false;

  // KAN-401: mirror the registration form so the Edit Profile name field
  // accepts exactly the same names (letters, spaces, hyphens, apostrophes,
  // accented letters).
  private namePattern = /^[a-zA-ZÀ-ÿ\s'\-]+$/;

  constructor(
    private fb: FormBuilder,
    private profileService: ProfileService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.currentUser = this.profileService.getCurrentUser();
    
    if (!this.currentUser) {
      this.router.navigate(['/auth/login']);
      return;
    }
    
    // Initialize form with current user data
    this.profileForm = this.fb.group({
      name: [this.currentUser.name, [Validators.required, Validators.minLength(2), Validators.maxLength(100), Validators.pattern(this.namePattern)]],
      email: [this.currentUser.email, [Validators.required, Validators.email]],
      location: [this.currentUser.location || '']
    });
  }

  onSubmit(): void {
    if (this.profileForm.invalid || !this.currentUser) {
      return;
    }
    
    this.isLoading = true;
    this.errorMessage = null;
    this.successMessage = null;
    
    const profileData = {
      name: this.profileForm.get('name')?.value,
      location: this.profileForm.get('location')?.value
    };
    
    this.profileService.updateProfile(this.currentUser.id, profileData)
      .pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: (response) => {
          this.isLoading = false;
          this.successMessage = response.message || 'Profile updated successfully';

          // KAN-400: sync the new name/location into the CANONICAL user cache and
          // the reactive current-user stream. Merge onto the existing cached user
          // so multi-org context (organizations / currentOrgRole / currentOrgId)
          // is preserved, and overlay the just-submitted values (the backend does
          // not return them under a stable `.user` shape). This makes the sidebar
          // (name + avatar initial) and profile header re-render instantly and
          // survive a plain refresh — no logout required.
          const base = this.profileService.getCurrentUser() ?? this.currentUser;
          if (base) {
            this.profileService.setCurrentUser({
              ...base,
              name: profileData.name,
              location: profileData.location,
            });
          }
          this.cdr.markForCheck();
        },
        error: (error) => {
          this.isLoading = false;
          this.errorMessage = error.error?.message || 'Failed to update profile';
          console.error('Profile update error:', error);
          this.cdr.markForCheck();
        }
      });
  }
} 